import { useRef, useState } from 'react';

import {
  Avatar,
  Box,
  Chip,
  Divider,
  Link,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import Layout from '@/components/layout/Layout';
import CamShell, { type CamNavItem } from '@/cam-admin/CamShell';
import { CAM_COMPACT } from '@/cam-admin/responsive';
import { AddChip } from '@/cam-admin/control-panel/bits';
import LeaveCover from '@/cam-admin/care-circles/LeaveCover';
import SearchField from '@/components/mui/SearchField';
import ScheduleCoverDrawer, {
  type CoverFormFields,
} from '@/cam-admin/care-circles/ScheduleCoverDrawer';
import {
  CARE_CIRCLES,
  CASE_MANAGERS,
  CASE_MANAGER_NAMES,
  SIGNED_IN_CAM,
  type CareCircleRow,
  type CircleCaseManager,
} from '@/cam-admin/care-circles/fixtures';
import {
  COVER_PERIODS,
  defaultAssignments,
  type CoverPeriod,
} from '@/cam-admin/care-circles/LeaveCover.fixtures';

const COLUMNS = [
  'Carer',
  'Care recipient',
  'Email',
  'Care circle ID',
  'Case manager',
];

type CareCirclesTab = 'circles' | 'my-leave' | 'cover';

// Leave is two tabs because it is two jobs: booking your own absence, and
// holding somebody else's circles while they are away. They were 'Caseloads'
// (which showed leave, not caseloads) and an undesigned 'Covering for others',
// so the split was right and only the names were wrong. Whose caseload a circle
// is belongs to the filter on Circles, which is where the word means something.
const TABS: { id: CareCirclesTab; label: string }[] = [
  { id: 'circles', label: 'Circles' },
  { id: 'my-leave', label: 'My leave' },
  { id: 'cover', label: 'Cover' },
];

// The tab ids shown by default. A caller can pass a subset via `tabs` to descope
// the page — the Descoping CAM prototype shows Circles only.
const ALL_TABS: CareCirclesTab[] = TABS.map((t) => t.id);

// Whose caseload the table is showing. Defaults to the signed-in care expert,
// because your own circles are what you open this page for; 'all' and
// 'unassigned' are the two answers a name cannot give, and unassigned is the
// one worth having — those circles belong to nobody.
const MINE = 'mine';
const ALL = 'all';
const UNASSIGNED = 'unassigned';

const caseloadOptions = () => [
  { value: MINE, label: `Your caseload (${SIGNED_IN_CAM})` },
  // Second, not last. The page opens on your own circles, so everything else
  // is one click away and 'all of them' is the click most often wanted — a new
  // circle nobody has picked up is in there and in nobody's caseload.
  { value: ALL, label: 'All circles' },
  ...CASE_MANAGER_NAMES()
    .filter((n) => n !== SIGNED_IN_CAM)
    .map((n) => ({ value: n, label: n })),
  { value: UNASSIGNED, label: 'Unassigned' },
];

const inCaseload = (circle: CareCircleRow, caseload: string) => {
  if (caseload === ALL) return true;
  if (caseload === UNASSIGNED) return !circle.caseManager;
  const name = caseload === MINE ? SIGNED_IN_CAM : caseload;
  return circle.caseManager?.name === name;
};

const matches = (circle: CareCircleRow, query: string) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    circle.carer,
    circle.careRecipient,
    circle.email,
    circle.id,
    circle.caseManager?.name ?? '',
  ].some((field) => field.toLowerCase().includes(q));
};

// Harness-only: seed the index into one of its states.
export type CareCirclesInitial = {
  // No circles at all — the account has none yet.
  empty?: boolean;
  // Pre-fill the search box (e.g. to show the no-results state).
  search?: string;
  // Which caseload the table opens on. Defaults to the signed-in CAM's own, so
  // a scene wanting the whole index has to ask for it.
  caseload?: string;
};

/**
 * The CAM admin's care circles index: the landing page behind the "Care
 * circles" nav item. A searchable table of every care circle, from which a CAM
 * opens one into its control panel. Presentational — routing (row → control
 * panel, nav rail, bell) is injected via `onNavigate` / `onOpenCircle`, so the
 * page renders straight into Storybook without a router.
 */
const CareCircles = ({
  initial,
  onNavigate,
  onOpenCircle,
  tabs = ALL_TABS,
  nav,
  activeNav,
}: {
  initial?: CareCirclesInitial;
  onNavigate?: (path: string) => void;
  onOpenCircle?: (circle: CareCircleRow) => void;
  tabs?: CareCirclesTab[];
  // Override the CAM nav rail / active item — the descoping build passes its own
  // rail (with a Your caseloads item). Left undefined, CamShell uses its
  // defaults.
  nav?: CamNavItem[];
  activeNav?: string;
}) => {
  const base = initial?.empty ? [] : CARE_CIRCLES;
  // The tabs actually rendered, in canonical order. Passing a subset descopes
  // the page; with one tab left the tab bar is hidden and Schedule cover (a
  // cover action) goes with it.
  const shownTabs = TABS.filter((t) => tabs.includes(t.id));
  const showCover = tabs.includes('my-leave');
  const [query, setQuery] = useState(initial?.search ?? '');
  const [caseload, setCaseload] = useState(initial?.caseload ?? MINE);
  // Assignment is editable in the table, so it lives here rather than being
  // read straight off the fixture. Keyed by circle id, and only for the rows
  // that have been changed.
  const [owners, setOwners] = useState<
    Record<string, CircleCaseManager | undefined>
  >({});
  // Which row's case-manager picker is open: the chip that was clicked, and
  // the circle it belongs to.
  const [picker, setPicker] = useState<{
    id: string;
    anchor: HTMLElement;
  } | null>(null);
  const [tab, setTab] = useState<CareCirclesTab>('circles');

  // Cover periods live here so scheduling and editing can update them. The
  // Schedule cover / edit drawer renders through CamShell's overlay slot
  // (full-frame, over the nav) and is shared by every tab's button.
  const [periods, setPeriods] = useState<CoverPeriod[]>(COVER_PERIODS);
  const [coverForm, setCoverForm] = useState<{
    mode: 'create' | 'edit';
    id?: string;
  } | null>(null);
  const lastCoverForm = useRef(coverForm);
  if (coverForm) lastCoverForm.current = coverForm;
  const shownCover = coverForm ?? lastCoverForm.current;
  const editingPeriod =
    shownCover?.mode === 'edit'
      ? periods.find((p) => p.id === shownCover.id)
      : undefined;
  const coverIdRef = useRef(0);

  const submitCover = (fields: CoverFormFields) => {
    if (shownCover?.mode === 'edit' && shownCover.id) {
      setPeriods(
        periods.map((p) =>
          p.id === shownCover.id
            ? {
                ...p,
                start: fields.start,
                end: fields.end,
                mainCover: fields.mainCover,
                handoverNote: fields.handoverNote || undefined,
              }
            : p,
        ),
      );
    } else {
      coverIdRef.current += 1;
      setPeriods([
        ...periods,
        {
          id: `cp-new-${coverIdRef.current}`,
          // Scheduling cover books your own leave, never a colleague's.
          owner: SIGNED_IN_CAM,
          start: fields.start,
          end: fields.end,
          mainCover: fields.mainCover,
          status: 'upcoming',
          handoverNote: fields.handoverNote || undefined,
          assignments: defaultAssignments(fields.mainCover),
        },
      ]);
    }
    setCoverForm(null);
  };

  // The fixture with the table's own edits laid over it, so filtering and the
  // unassigned count both see what the table is actually showing.
  const circles = base.map((c) => ({
    ...c,
    caseManager: c.id in owners ? owners[c.id] : c.caseManager,
  }));

  const rows = circles.filter(
    (c) => inCaseload(c, caseload) && matches(c, query),
  );

  const assign = (id: string, manager?: CircleCaseManager) => {
    setOwners((prev) => ({ ...prev, [id]: manager }));
    setPicker(null);
  };

  // Circles nobody owns. A new sign-up has no case manager, so it sits in
  // nobody's caseload and the default view cannot show it: somebody has to be
  // told it is there rather than having to go and look.
  const unassigned = circles.filter((c) => !c.caseManager).length;
  const flagUnassigned = unassigned > 0 && caseload !== UNASSIGNED;

  return (
    <CamShell
      activeNav={activeNav ?? 'Care circles'}
      nav={nav}
      onNavigate={onNavigate}
      onBell={() => onNavigate?.('/care-search/notifications')}
      overlays={
        <>
          {/* One menu for every row: the picker state says which cell opened it,
            so eight rows do not mount sixteen menus. */}
          <Menu
            anchorEl={picker?.anchor ?? null}
            open={Boolean(picker)}
            onClose={() => setPicker(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {picker
              ? [
                  ...CASE_MANAGERS.map((m) => (
                    <MenuItem
                      key={m.name}
                      selected={
                        circles.find((c) => c.id === picker.id)?.caseManager
                          ?.name === m.name
                      }
                      onClick={() => assign(picker.id, m)}
                    >
                      <Stack
                        sx={{ alignItems: 'center' }}
                        direction='row'
                        spacing={1.5}
                      >
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: 12,
                            bgcolor: 'custom.neutral.300',
                            color: 'common.white',
                          }}
                        >
                          {m.initials}
                        </Avatar>
                        <Typography variant='body2'>{m.name}</Typography>
                      </Stack>
                    </MenuItem>
                  )),
                  <Divider key='divider' />,
                  <MenuItem
                    key='unassign'
                    onClick={() => assign(picker.id, undefined)}
                  >
                    <Typography variant='body2' color='error.main'>
                      Unassign
                    </Typography>
                  </MenuItem>,
                ]
              : null}
          </Menu>

          <ScheduleCoverDrawer
            open={Boolean(coverForm)}
            mode={shownCover?.mode ?? 'create'}
            initial={
              editingPeriod
                ? {
                    start: editingPeriod.start,
                    end: editingPeriod.end,
                    mainCover: editingPeriod.mainCover,
                    handoverNote: editingPeriod.handoverNote ?? '',
                  }
                : undefined
            }
            assignments={editingPeriod?.assignments}
            existingPeriods={periods}
            excludeId={shownCover?.mode === 'edit' ? shownCover.id : undefined}
            // Opened from the Cover tab, the period is somebody else's leave.
            readOnly={Boolean(
              editingPeriod && editingPeriod.owner !== SIGNED_IN_CAM,
            )}
            onClose={() => setCoverForm(null)}
            onSubmit={submitCover}
            onCancelPeriod={
              editingPeriod && editingPeriod.status === 'upcoming'
                ? () => {
                    setPeriods((ps) =>
                      ps.filter((p) => p.id !== editingPeriod.id),
                    );
                    setCoverForm(null);
                  }
                : undefined
            }
          />
        </>
      }
    >
      <Layout sx={{ [CAM_COMPACT]: { p: 2 } }}>
        {/* Title + a persistent Schedule cover action, above the tabs so it's
            available from every care-circles tab. */}
        <Stack
          sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
          direction='row'
          spacing={2}
        >
          <Typography variant='h3' color='primary.main'>
            Care circles
          </Typography>
          {showCover && (
            <Button
              variant='primary'
              size='sm'
              ariaLabel='Schedule cover'
              onPress={() => setCoverForm({ mode: 'create' })}
            >
              Schedule cover
            </Button>
          )}
        </Stack>

        {/* One tab left → no tab bar; the page reads as a single view. MUI's
            sliding indicator measures in visual pixels, which lands under the
            wrong tab inside the scaled laptop frame — use a CSS underline on the
            selected tab instead (matches the control panel's tabs). */}
        {shownTabs.length > 1 && (
          <>
            <Tabs
              slotProps={{ indicator: { sx: { display: 'none' } } }}
              value={shownTabs.findIndex((t) => t.id === tab)}
              onChange={(_, i) => setTab(shownTabs[i].id)}
              variant='scrollable'
              scrollButtons={false}
            >
              {shownTabs.map((t) => (
                <Tab
                  key={t.id}
                  label={t.label}
                  sx={{
                    '&.Mui-selected': {
                      boxShadow: (theme) =>
                        `inset 0 -2px 0 ${theme.palette.primary.main}`,
                    },
                  }}
                />
              ))}
            </Tabs>
            <Divider />
          </>
        )}

        {tab === 'my-leave' || tab === 'cover' ? (
          <LeaveCover
            periods={periods}
            direction={tab === 'my-leave' ? 'mine' : 'covering'}
            onScheduleCover={() => setCoverForm({ mode: 'create' })}
            onOpen={(id) => setCoverForm({ mode: 'edit', id })}
          />
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              pt: 3,
            }}
          >
            <Stack
              sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 3 }}
              direction='row'
              spacing={2}
              useFlexGap
            >
              <Box sx={{ width: 320, maxWidth: '100%' }}>
                <SearchField
                  placeholder='Search care circles'
                  value={query}
                  onChange={setQuery}
                />
              </Box>
              {/* Whose caseload. This is where 'caseload' earns the word: the
                  page is every circle, and the filter is the part of it that
                  is somebody's. */}
              <TextField
                select
                size='xsmall'
                value={caseload}
                onChange={(e) => setCaseload(e.target.value)}
                sx={{ width: 260 }}
              >
                {caseloadOptions().map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>

              {/* Says it rather than waiting to be asked. Coral because an
                  unclaimed circle is somebody waiting on us, not a neutral
                  fact about the list. */}
              {flagUnassigned && (
                <Link
                  component='button'
                  type='button'
                  variant='body2'
                  sx={{ color: 'custom.coral.700' }}
                  onClick={() => setCaseload(UNASSIGNED)}
                >
                  {unassigned === 1
                    ? '1 circle has no case manager'
                    : `${unassigned} circles have no case manager`}
                </Link>
              )}
            </Stack>

            {rows.length === 0 ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  px: 3,
                }}
              >
                <Typography sx={{ mb: 1 }} variant='h3' color='primary.main'>
                  {circles.length === 0
                    ? 'No care circles yet'
                    : 'No matching care circles'}
                </Typography>
                <Typography variant='body1' color='text.secondary'>
                  {circles.length === 0
                    ? 'Care circles will appear here as they’re created.'
                    : 'Try a different name, email or care circle ID.'}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 1 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {COLUMNS.map((col) => (
                        <TableCell key={col}>
                          <Typography variant='body2' color='text.secondary'>
                            {col}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((circle) => (
                      <TableRow
                        key={circle.id}
                        hover
                        onClick={() => onOpenCircle?.(circle)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{circle.carer}</TableCell>
                        <TableCell>{circle.careRecipient}</TableCell>
                        <TableCell>{circle.email}</TableCell>
                        <TableCell>{circle.id}</TableCell>
                        {/* Assign from the row. stopPropagation on the cell,
                            because the row opens the circle and picking a
                            colleague is not that. */}
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {circle.caseManager ? (
                            <Chip
                              size='small'
                              variant='outlined'
                              avatar={
                                <Avatar
                                  sx={{
                                    bgcolor: 'custom.neutral.300',
                                    color: 'common.white',
                                  }}
                                >
                                  {circle.caseManager.initials}
                                </Avatar>
                              }
                              label={circle.caseManager.name}
                              onClick={(e) =>
                                setPicker({
                                  id: circle.id,
                                  anchor: e.currentTarget,
                                })
                              }
                            />
                          ) : (
                            <AddChip
                              label='Assign'
                              onClick={(e) =>
                                setPicker({
                                  id: circle.id,
                                  anchor: e.currentTarget,
                                })
                              }
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}
      </Layout>
    </CamShell>
  );
};

export default CareCircles;
